import { useMemo } from 'react';
import { useLoadoutStore } from '../store/loadoutSlice';
import { useSocketsStore } from '../store/socketsSlice';
import {
  TRINKET_MAJOR_REGISTRY,
  TRINKET_MINOR_REGISTRY,
} from '../data/trinkets.config';
import { SKETCH_REGISTRY } from '../data/sketches.config';
import { SPELL_REGISTRY } from '../data/spells.config';
import { MODIFIER_REGISTRY } from '../data/modifiers.config';
import { ITEMS } from '../data/inventory.config';
import {
  describeTrinketCurrent,
  describeSketch,
  describeSocketedModifiers,
  type DescLine,
} from '../services/TrinketDescriptionService';
import {
  describeSpell,
  trinketCard,
  cardToData,
} from '../services/CardDescriptionService';
import type {
  BoardRef,
  SlotRef,
  LoadoutBoardSlots,
  SocketSlotData,
} from '../components/DOMElements/LoadoutBoard';
import type { ItemCardData } from '../components/DOMElements/ItemCard';

export interface LoadoutBoardData {
  slots: LoadoutBoardSlots;
  sockets: (SocketSlotData | null)[];
  /** Cumulative effect lines for an equipped trinket (+ its sockets). */
  trinketLines: (modelId: string) => DescLine[];
  /** Detail card for any board cell (slot or socket). */
  getCard: (ref: BoardRef) => ItemCardData | null;
}

/**
 * Shared read model for the loadout board: equipped slots, their sockets, and
 * the detail card for any focused cell. Consumed by both the hub LoadoutScreen
 * (full editing) and the in-run EquipmentScreen (read-only + socketing).
 */
export function useLoadoutBoardData(): LoadoutBoardData {
  const equipped = useLoadoutStore((s) => s.equipped);
  const majorTrinkets = useLoadoutStore((s) => s.majorTrinkets);
  const minorTrinkets = useLoadoutStore((s) => s.minorTrinkets);
  const sketches = useLoadoutStore((s) => s.sketches);
  const socketed = useSocketsStore((s) => s.socketed);

  const slots = useMemo<LoadoutBoardSlots>(() => {
    const majorOwned =
      equipped.major !== null && majorTrinkets[equipped.major] !== undefined;
    return {
      major: {
        hasItem: majorOwned,
        icon: majorOwned
          ? TRINKET_MAJOR_REGISTRY[equipped.major!]?.icon
          : undefined,
      },
      spells: equipped.slots.map((slot) => ({
        hasItem: !!slot.spellId,
        icon: slot.spellId ? SPELL_REGISTRY[slot.spellId]?.icon : undefined,
      })),
      minors: equipped.slots.map((slot) => {
        const owned =
          slot.minorId !== null && minorTrinkets[slot.minorId] !== undefined;
        return {
          hasItem: owned,
          icon: owned ? TRINKET_MINOR_REGISTRY[slot.minorId!]?.icon : undefined,
        };
      }),
      sketches: (['sketch1', 'sketch2'] as const).map((key) => {
        const id = equipped.sketches[key];
        const owned = id !== null && sketches.includes(id);
        return {
          hasItem: owned,
          icon: owned ? SKETCH_REGISTRY[id!]?.icon : undefined,
        };
      }),
    };
  }, [equipped, majorTrinkets, minorTrinkets, sketches]);

  const sockets = useMemo<(SocketSlotData | null)[]>(
    () =>
      equipped.slots.map((slot) => {
        const minorId = slot.minorId;
        if (!minorId || minorTrinkets[minorId] === undefined) {
          return null;
        }
        const def = TRINKET_MINOR_REGISTRY[minorId];
        if (!def?.socketSlots) {
          return null;
        }
        return {
          minorId,
          slotCount: def.socketSlots,
          socketedIds: socketed[minorId] ?? [],
        };
      }),
    [equipped.slots, minorTrinkets, socketed],
  );

  const trinketLines = (modelId: string): DescLine[] => {
    const majorLevel = majorTrinkets[modelId];
    if (majorLevel !== undefined) {
      const def = TRINKET_MAJOR_REGISTRY[modelId];
      return def ? describeTrinketCurrent(def, majorLevel) : [];
    }
    const minorLevel = minorTrinkets[modelId];
    if (minorLevel !== undefined) {
      const def = TRINKET_MINOR_REGISTRY[modelId];
      const base = def ? describeTrinketCurrent(def, minorLevel) : [];
      return [...base, ...describeSocketedModifiers(socketed[modelId] ?? [])];
    }
    return [];
  };

  const resolveEquippedCard = (slot: SlotRef): ItemCardData | null => {
    if (slot.kind === 'major') {
      const id = equipped.major;
      const level = id !== null ? majorTrinkets[id] : undefined;
      const def = id !== null ? TRINKET_MAJOR_REGISTRY[id] : undefined;
      if (id === null || level === undefined || !def) {
        return null;
      }
      const card = trinketCard(id, level, true);
      return {
        icon: def.icon,
        name: def.name,
        subtitle: `Niveau ${level}`,
        ...(card ? cardToData(card) : { lines: trinketLines(id) }),
      };
    }
    if (slot.kind === 'spell') {
      const spellId = equipped.slots[slot.index]?.spellId;
      const def = spellId ? SPELL_REGISTRY[spellId] : undefined;
      if (!def) {
        return null;
      }
      return {
        icon: def.icon,
        name: def.name,
        ...cardToData(describeSpell(def)),
      };
    }
    if (slot.kind === 'minor') {
      const id = equipped.slots[slot.index]?.minorId;
      const level = id ? minorTrinkets[id] : undefined;
      const def = id ? TRINKET_MINOR_REGISTRY[id] : undefined;
      if (!id || level === undefined || !def) {
        return null;
      }
      const card = trinketCard(id, level, false);
      return {
        icon: def.icon,
        name: def.name,
        subtitle: `Niveau ${level}`,
        // Displayed lines keep socket modifiers; rootCard drives the related cards.
        lines: trinketLines(id),
        ...(card ? { stats: card.stats, rootCard: card } : {}),
      };
    }
    const id = equipped.sketches[slot.index === 0 ? 'sketch1' : 'sketch2'];
    const def = id && sketches.includes(id) ? SKETCH_REGISTRY[id] : undefined;
    return def
      ? {
          icon: def.icon,
          name: def.name,
          subtitle: def.emotion,
          lines: describeSketch(def),
        }
      : null;
  };

  const getCard = (ref: BoardRef): ItemCardData | null => {
    if (ref.kind === 'socket') {
      const modifierId = (socketed[ref.minorId] ?? [])[ref.socketIndex];
      const mod = modifierId ? MODIFIER_REGISTRY[modifierId] : undefined;
      return mod
        ? {
            icon: mod.icon ?? ITEMS[`boost-${modifierId}`]?.sprite,
            name: mod.name,
            subtitle: mod.displayTemplate,
          }
        : null;
    }
    return resolveEquippedCard(ref);
  };

  return { slots, sockets, trinketLines, getCard };
}
